import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'

/**
 * A donde vuelve Google después de «Conectar cuenta de Google» en
 * Integraciones (`/integraciones/google`).
 *
 * Google trae un `code` de un solo uso y el `state` que armó el backend. Esta
 * página solo se los pasa al backend —que los canjea con el client secret y
 * guarda el refresh token en Secrets Manager— y vuelve a Integraciones. El
 * token no llega nunca al navegador.
 *
 * El `useRef` no es cosmético: en desarrollo React monta dos veces, y el
 * segundo canje del mismo código fallaría —el `state` es de un solo uso— y
 * pintaría un error sobre una conexión que salió bien.
 */
interface Resultado {
  email: string
  refresh_vence_at?: string
  carpeta?: { ok: boolean; nombre: string; error: string }
}

export default function GoogleCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const enviado = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [hecho, setHecho] = useState<Resultado | null>(null)

  useEffect(() => {
    if (enviado.current) return
    enviado.current = true
    const errorDeGoogle = params.get('error')
    if (errorDeGoogle) {
      setError(errorDeGoogle === 'access_denied'
        ? 'Se canceló la conexión en la pantalla de Google.'
        : `Google devolvió un error: ${errorDeGoogle}`)
      return
    }
    apiClient.post<Resultado>('/integrations/drive/oauth/callback', {
      code: params.get('code') ?? '', state: params.get('state') ?? '',
    })
      .then((r) => setHecho(r.data))
      .catch((e: unknown) => setError(errorMessage(e, 'No se pudo completar la conexión.')))
  }, [params])

  // Con todo en orden, de vuelta a Integraciones sin un clic de más.
  useEffect(() => {
    if (!hecho || hecho.refresh_vence_at || (hecho.carpeta && !hecho.carpeta.ok)) return
    const t = setTimeout(() => navigate('/integrations', { replace: true }), 2500)
    return () => clearTimeout(t)
  }, [hecho, navigate])

  return (
    <div className="mx-auto mt-10 flex max-w-lg flex-col gap-3">
      <div className="card p-6">
        <h2 className="text-card font-extrabold text-ink">Conexión de Google Drive</h2>
        {!error && !hecho && (
          <p className="mt-2 text-body text-ink-muted">Completando la conexión con Google…</p>
        )}
        {error && (
          <p className="mt-2 text-body text-danger">{error}</p>
        )}
        {hecho && (
          <div className="mt-2 flex flex-col gap-2 text-body text-ink">
            <p>
              Conectada <b className="font-mono">{hecho.email}</b>. Desde ahora el bot sube los
              documentos con esta cuenta.
            </p>
            {hecho.carpeta && (hecho.carpeta.ok
              ? <p className="text-brand-hover">Puede archivar en «{hecho.carpeta.nombre}».</p>
              : <p className="text-danger">Pero no puede archivar en la carpeta configurada: {hecho.carpeta.error}</p>)}
            {hecho.refresh_vence_at && (
              <p className="text-warn">
                Google limita este acceso hasta el {new Date(hecho.refresh_vence_at).toLocaleDateString('es-PE')}:
                la app de Google está en modo «prueba». Pásala a producción o a uso interno, o el bot
                dejará de archivar ese día.
              </p>
            )}
          </div>
        )}
        <Link to="/integrations" className="btn-ghost mt-4 inline-block">Volver a Integraciones</Link>
      </div>
    </div>
  )
}
